import  { useState, useEffect } from "react";

import { Card, CardContent as CardBody } from '@shadcn/components/ui/card'

import { useUrlState } from "../../../helpers/useUrlState";
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import moment from 'moment';
import useQuery from "../../../helpers/JIFI/useQuery";
import useSKUStatus from "../helpers/useSKUStatus";

import { Row } from "@shadcn/components/ui/row";
import { Col } from "@shadcn/components/ui/col";
import * as XLSX from 'xlsx';


import SOHFilterBar from './components/SOHFilterBar';
import SOHTable from './components/SOHTable';




function generateMonthYearStrings(startYear, endYear) {
  const startDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
  const endDate = moment().add(1, 'day').format('YYYY-MM-DD');;

  const startOfYear = startDate ? moment.tz(`${startDate} 00:00`, "Australia/Melbourne").startOf('day').tz("Etc/GMT") : null
  const endOfYear = endDate ? moment.tz(`${endDate} 12:00`, "Australia/Melbourne").endOf('day').tz("Etc/GMT") : null

  const months = [];

  while (startOfYear.isBefore(endOfYear)) {
    months.push(startOfYear.format('YYYY-MM'));
    startOfYear.add(1, 'month');
  }

  
  months.push(endOfYear.format('YYYY-MM'));

  return [...new Set(months)];
}


function getWeekKeys(weeklyData) {
  if (!weeklyData) return [];
  return Object.keys(weeklyData).filter(key => key !== 'mtd');
}


function getForecastMonths(forecasts) {
  if (!forecasts || !forecasts.AU) return [];
  return Object.keys(forecasts.AU).sort();
}


function getForecastRegions(forecasts) {
  if (!forecasts) return [];
  return Object.keys(forecasts);
}

const SOHDetails = () => {
  const initialState = {
    region: 'AU'
  }
  const [stateData, setStateData] = useUrlState(initialState);
  const [refresh, setRefresh] = useState(true)
  const [isExporting, setIsExporting] = useState(false);
  
  const {  region } = stateData;

  const findSKUStatus = useSKUStatus();

  const [stockStatus, stockStatusState] = useQuery({
    url: `/reports/soh`,
    refresh,
    queryData: {
      region
    }
  })

  


  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [parseProducts, setParsedProducts] = useState([])
  const [weekKeys, setWeekKeys] = useState([])
  const [forecastMonths, setForecastMonths] = useState([])
  const [forecastRegions, setForecastRegions] = useState([])
  const fullSelectedProduct = parseProducts.find(product => product.id === selectedProduct?.value)
  const productOptions = parseProducts.map(product => ({ value: product.id, label: product.name }))

  
  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Off Site', label: 'Off Site' },
    { value: 'Out of Stock', label: 'Out of Stock' }
  ];

  
  const initialColumns = {
    status: true,
    color: true,
    rrp: false,
    auCog: false,
    usCog: false,
    monthlyData: true,
    weeklyData: true,
    forecastData: true,
    stockData: true
  };
  const [visibleColumns, setVisibleColumns] = useState(initialColumns);

  // Column options for the dropdown (removed category and subCategory)
  const columnOptions = [
    { key: 'status', label: 'Status' },
    { key: 'color', label: 'Color' },
    { key: 'rrp', label: 'RRP' },
    { key: 'auCog', label: 'AU COG' },
    { key: 'usCog', label: 'US COG' },
    { key: 'monthlyData', label: 'Monthly Data' },
    { key: 'weeklyData', label: 'Weekly Data' },
    { key: 'forecastData', label: 'Forecast Data' },
    { key: 'stockData', label: 'Stock Data' }
  ];
  const [products, productState] = useQuery({
    url: `/product/findAll`,
    refresh,
  });

  
  
  useEffect(() => {
    if (products && productState === 'success') {
      let tempProd = products?.data?.map(product => {
        let variants = []
        product.variants.map(variant => {
          variants.push(variant.sku)
        })
        return {
          id: product._id,
          name: product.name,
          variants,
          variantsFull: product.variants,
          category: product.category,
          subCategory: product.subCategory,
        
          newProductCategory: product.newProductCategory
        }
      }) || []

      tempProd = tempProd.sort((a, b) => {
        // Compare by category
        if (a.category < b.category) return -1;
        if (a.category > b.category) return 1;

        // If categories are equal, compare by name
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        return 0;
      });

      setParsedProducts(tempProd)
    }
  }, [products])

  useEffect(() => {
    if (stockStatus && stockStatus.length > 0) {
      const firstItem = stockStatus[0];
      
      
      if (firstItem.weeklyData) {
        const weeks = getWeekKeys(firstItem.weeklyData);
        setWeekKeys(weeks);
      }
      
      
      if (firstItem.forecasts) {
        const regions = getForecastRegions(firstItem.forecasts);
        setForecastRegions(regions);
        
        const months = getForecastMonths(firstItem.forecasts);
        setForecastMonths(months);
      }
    }
  }, [stockStatus]);

  const loadingState = stockStatusState === 'success' && productState === 'success'

  
  const setRegion = (val) => {
    setStateData(prev => ({
      ...prev,
      region: val
    }))
  }

  const allMonthStrings = generateMonthYearStrings()

  
  const groupData = (data) => {
    if (!data) return {};
    return data.reduce((acc, item) => {
      const category = item.productCategory || 'Uncategorized';
      const subCategory = item.productSubCategory || 'No Sub-category';
      if (!acc[category]) {
        acc[category] = {};
      }
      if (!acc[category][subCategory]) {
        acc[category][subCategory] = [];
      }
      acc[category][subCategory].push(item);
      return acc;
    }, {});
  };


  const parsedLines = stockStatus?.sort((a, b) => a._id - b._id).map(status => {
    if (status._id === '' || status._id === '.') {
      return undefined
    }
    if (fullSelectedProduct && !fullSelectedProduct.variants.includes(status._id)) {
      return undefined
    }
    if (searchTerm !== '' && !status._id.toUpperCase().includes(searchTerm.toUpperCase())) {
      return undefined
    }
    if (status.salesVelocity === undefined || status.salesVelocity.filter(val => val).length === 0) {
      return undefined
    }
    
    const product = parseProducts?.find(product => product.variants.includes(status._id))
    const fullVariant = product?.variantsFull.find(variant => variant.sku === status._id)

    const groupedMonthCounts = status.salesVelocity.map((val, i) => {
      
        return {
          count: Math.round(val),
          month: status.monthYear[i]
        }
      
      
    })

    const allMonths = allMonthStrings.map(val => {
      const month = groupedMonthCounts.find(month => month.month === val)
      return {
        count: month?.count || 0,
        month: val
      }
    })

    
    const currentRegion = region || 'AU';
    const currentForecast = status.forecasts?.[currentRegion] || {};
    const currentMonth = forecastMonths && forecastMonths.length > 0 ? forecastMonths[0] : '';
    const forecastCurrentMonth = currentForecast[currentMonth] || 0;
    
    
    const totalStock = status.totalStock || 0;
    const totalUpcoming = status.totalUpcoming || 0;
    const totalStockAfterIncoming = totalStock + totalUpcoming - forecastCurrentMonth;

    
    let avgForecast = 0;
    let count = 0;
    
    if (forecastMonths && forecastMonths.length > 0) {
      for (let i = 0; i < Math.min(3, forecastMonths.length); i++) {
        if (currentForecast[forecastMonths[i]] !== undefined) {
          avgForecast += currentForecast[forecastMonths[i]];
          count++;
        }
      }
      
    
      avgForecast = count > 0 ? avgForecast / count : 0;
    }
    
    
    const monthCover = avgForecast > 0 ? totalStockAfterIncoming / avgForecast : 0;

    
    return {
      sku: status._id,
      region: status.region,
      monthCounts: allMonths,
      
      productCategory: product?.newProductCategory?.name,
      productName: product?.name, 
      productSubCategory: product?.subCategory,
      color: fullVariant?.color,
      retailPrice: fullVariant?.retailPrice,
      auCog: fullVariant?.auCog,
      usCog: fullVariant?.usCog,
      price: (fullVariant?.shopifyData || {})[region?.replace('Online', '')]?.price,
      weeklyData: status.weeklyData || {},
      forecasts: status.forecasts || {},
      
      totalStock: status.totalStock || 0,
      committed: status.committed || 0,
      committedByLocation: status.committedByLocation || {},
      unallocated: status.unallocated || 0,
      spotCover: status.spotCover || 0,
      fwdCover: status.fwdCover || 0,
      totalUpcoming: status.totalUpcoming || 0,
      totalStockAfterIncoming,
      monthCover: monthCover.toFixed(1),
      lyMtd: status.lyMtd || 0,
      varLy: status.varLy || 0,
      varLyPercentage: status.varLyPercentage || 0
    }
  }).flat().filter(val => val).filter(item => {
    
    if (selectedStatus) {
      return findSKUStatus(item.sku, region) === selectedStatus.value;
    }
    return true;
  }).sort((a, b) => {

    const categoryComparison = a?.productCategory?.localeCompare(b?.productCategory);
    if (categoryComparison !== 0) {
      return categoryComparison;
    }


    return a?.sku?.localeCompare(b?.sku);
  });

  
  const groupedData = groupData(parsedLines);

  const handleExportSOH = async () => {
    if (!parsedLines || parsedLines.length === 0) {
      alert('No data available to export');
      return;
    }

    setIsExporting(true);

    try {
      const workbook = XLSX.utils.book_new();

      
      const exportData = [];

      
      const headers = [];
      const columnWidths = [];

      
      headers.push('SKU', 'Product Name');
      columnWidths.push({ wch: 15 }, { wch: 25 });

      
      if (visibleColumns.status) {
        headers.push('Status');
        columnWidths.push({ wch: 12 });
      }

      if (visibleColumns.color) {
        headers.push('Color');
        columnWidths.push({ wch: 12 });
      }

      if (visibleColumns.rrp) {
        headers.push('RRP');
        columnWidths.push({ wch: 10 });
      }

      if (visibleColumns.auCog) {
        headers.push('AU COG');
        columnWidths.push({ wch: 10 });
      }

      if (visibleColumns.usCog) {
        headers.push('US COG');
        columnWidths.push({ wch: 10 });
      }

      
      if (visibleColumns.monthlyData) {
        headers.push(...allMonthStrings.map(month => `Month-${month}`));
        columnWidths.push(...allMonthStrings.map(() => ({ wch: 12 })));
      }

      
      if (visibleColumns.weeklyData) {
        headers.push(...weekKeys.map(week => `Week-${week.toUpperCase()}`), 'MTD');
        columnWidths.push(...weekKeys.map(() => ({ wch: 12 })), { wch: 10 });
      }

      
      if (visibleColumns.forecastData) {
        headers.push(...forecastRegions.flatMap(region =>
          forecastMonths.map(month => `Forecast-${month}(${region})`)
        ));
        columnWidths.push(...forecastRegions.flatMap(() =>
          forecastMonths.map(() => ({ wch: 15 }))
        ));
      }

      
      if (visibleColumns.stockData) {
        headers.push(
          'Total Stock',
          'Committed',
          'Unallocated',
          'Spot Cover',
          'Fwd Cover',
          'Total Upcoming',
          'Total Stock (After Incoming)',
          'Month Cover',
          'LY Sales',
          'Var LY Sales',
          'Var LY Sales %'
        );
        columnWidths.push(
          { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
          { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
        );
      }

      exportData.push(headers);


      parsedLines.forEach(status => {
        const row = [];


        row.push(status.sku, status.productName);


        if (visibleColumns.status) {
          row.push(findSKUStatus(status.sku, region));
        }

        if (visibleColumns.color) {
          row.push(status.color);
        }

        if (visibleColumns.rrp) {
          row.push(status.retailPrice);
        }

        if (visibleColumns.auCog) {
          row.push(status.auCog);
        }

        if (visibleColumns.usCog) {
          row.push(status.usCog);
        }

        
        if (visibleColumns.monthlyData) {
          row.push(...status.monthCounts.map(month => month.count === 0 ? '' : month.count));
        }

        
        if (visibleColumns.weeklyData) {
          row.push(
            ...weekKeys.map(weekKey =>
              status.weeklyData && status.weeklyData[weekKey] !== 0 ? status.weeklyData[weekKey] : ''
            ),
            status.weeklyData && status.weeklyData.mtd !== 0 ? status.weeklyData.mtd : ''
          );
        }

        
        if (visibleColumns.forecastData) {
          row.push(...forecastRegions.flatMap(forecastRegion =>
            forecastMonths.map(month =>
              status.forecasts && status.forecasts[forecastRegion] && status.forecasts[forecastRegion][month] !== 0
                ? status.forecasts[forecastRegion][month]
                : ''
            )
          ));
        }

        
        if (visibleColumns.stockData) {
          row.push(
            status.totalStock,
            status.committed,
            status.unallocated,
            status.spotCover,
            status.fwdCover,
            status.totalUpcoming,
            status.totalStockAfterIncoming,
            status.monthCover,
            status.lyMtd,
            status.varLy,
            status.varLyPercentage
          );
        }

        exportData.push(row);
      });

      
      const worksheet = XLSX.utils.aoa_to_sheet(exportData);

      
      worksheet['!cols'] = columnWidths;
      
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'SOH Details');

      
      const summaryData = [
        ['SOH Details Export Summary'],
        [''],
        ['Export Date:', new Date().toLocaleDateString()],
        ['Region:', region],
        ['Total SKUs:', parsedLines.length],
        [''],
        ['Filters Applied:'],
        ['Search Term:', searchTerm || 'None'],
        ['Selected Product:', fullSelectedProduct?.name || 'All Products'],
        [''],
        ['Data Includes:'],
        ['• Monthly sales velocity data'],
        ['• Weekly sales data'],
        ['• Forecast data by region'],
        ['• Stock levels and calculations'],
        ['• Last year comparisons'],
        [''],
        ['Note: Empty cells indicate zero values or no data available']
      ];
      
      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
      summaryWorksheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
      
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

      
      const dateStr = new Date().toISOString().split('T')[0];
      const productFilter = fullSelectedProduct ? `_${fullSelectedProduct.name.replace(/[^a-zA-Z0-9]/g, '')}` : '';
      const searchFilter = searchTerm ? `_search-${searchTerm.replace(/[^a-zA-Z0-9]/g, '')}` : '';
      const filename = `soh-details_${region}_${dateStr}${productFilter}${searchFilter}.xlsx`;

      
      XLSX.writeFile(workbook, filename);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%]">
          <Breadcrumbs title="Fulfillment" breadcrumbItem="SOH Details" />
          <Row>
            <Col>
              <Card className="mb-6">
                <CardBody>
                  <SOHFilterBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    region={region}
                    setRegion={setRegion}
                    selectedProduct={selectedProduct}
                    setSelectedProduct={setSelectedProduct}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    productOptions={productOptions}
                    statusOptions={statusOptions}
                    isExporting={isExporting}
                    loadingState={loadingState}
                    handleExportSOH={handleExportSOH}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    columnOptions={columnOptions}
                  />

                  {/* Table with improved styling */}
                  <SOHTable
                    groupedData={groupedData}
                    loadingState={loadingState}
                    allMonthStrings={allMonthStrings}
                    weekKeys={weekKeys}
                    forecastRegions={forecastRegions}
                    forecastMonths={forecastMonths}
                    findSKUStatus={findSKUStatus}
                    region={region}
                    visibleColumns={visibleColumns}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default SOHDetails;
